import type { Node as RuntimeNode, Edge as RuntimeEdge } from '../../../core/models/scene';
import { getRelationshipMapping } from '../../../lib/diagram/registries';
import type { Provenance } from '../../../core/data-profiles/provenance';
import type { GraphEdge, GraphNode } from '../../../lib/graph/types';
import {
  attrsWithRelationSemantics,
  relationSemanticsForSubtype,
  relationSubtypeForPredicate,
  relationSubtypeFromAttrs,
  storedRelationSemantics,
  stripRelationInternalAttrs,
} from '../../../lib/graph/relationshipSemantics';

export type ProcessNodeType = 'process' | 'decision' | 'terminator' | 'data' | 'document' | 'flowchart-shape' | 'bpmn-shape';
export type ProcessEdgeType = 'sequence_flow' | 'message_flow' | 'data_flow' | 'association';

export interface ProcessUniversalNodeProps {
  processId?: string;
  name?: string;
  description?: string;
  status?: string;
  owner?: string;
  duration?: string;
  triggerType?: string;
  outcomeType?: string;
  sla?: string;
  frequency?: string;
  systems?: string[];
  primarySystem?: string;
  controls?: string[];
  /**
   * Embedded GRC composite — shaped to match
   * `processComplianceFieldPack.controlPoints`. Populated by Phase 2
   * enrichment on the parent process record (not the per-step
   * activities). Every entry carries description/ref + optional
   * type/owner/frequency/testMethod/controlIds + a citation block.
   */
  controlPoints?: Array<Record<string, unknown>>;
  evidence?: Array<Record<string, unknown>>;
  /**
   * Embedded risk composite — shaped to match
   * `processRiskFieldPack.exceptions`. Same provenance model as
   * controlPoints; condition = risk label, recovery = risk description.
   */
  exceptions?: Array<Record<string, unknown>>;
  /**
   * Embedded obligations list — no field pack yet (planned), but the
   * data lands here so a future obligations pack renders cleanly.
   */
  obligations?: Array<Record<string, unknown>>;
  findings?: Array<Record<string, unknown>>;
  /** Denormalized ref lists for cross-process queries. */
  controlRefs?: string[];
  riskRefs?: string[];
  evidenceRefs?: string[];
  obligationRefs?: string[];
  /** At-a-glance counts for the inventory + audit surfaces. */
  stepCount?: number;
  controlCount?: number;
  riskCount?: number;
  evidenceCount?: number;
  obligationCount?: number;
  /** Phase 2 coverage signal — see EnricherService.CoverageSchema. */
  coverage?: {
    totalStepsInSource: number;
    capturedAsSubProcess: number;
    capturedAsProcedural: number;
    omittedNote: string | null;
  };
  inputs?: string;
  outputs?: string;
  automationStatus?: string;
  automationTool?: string;
  automationNotes?: string;
  delegation?: string;
  escalationPath?: string;
  riskLevel?: number;
  thirdPartyDependencies?: string[];
  botDependencies?: string[];
  modelDependencies?: string[];
  tags?: string[];
  linkedDiagramId?: string;
  /**
   * Universal provenance — citations, extraction confidence, run pointer.
   * Per docs/architecture/universal-ontology.md the ontology declares
   * provenance as a first-class attribute set; this is its concrete shape
   * on process records. Optional — pre-existing records have no provenance.
   * See docs/plans/2026-05-08-compass-source-review-and-folder-fidelity-plan.md §Design 1.
   */
  provenance?: Provenance;
  [key: string]: unknown;
}

export interface ProcessUniversalEdgeProps {
  label?: string;
  [key: string]: unknown;
}

export interface ProcessHydrator {
  toUniversal: (nodes: RuntimeNode[], edges: RuntimeEdge[]) => {
    nodes: GraphNode<ProcessNodeType, ProcessUniversalNodeProps>[];
    edges: GraphEdge<ProcessEdgeType, ProcessUniversalEdgeProps>[];
  };
  fromUniversal: (graph: {
    nodes: GraphNode<ProcessNodeType, ProcessUniversalNodeProps>[];
    edges: GraphEdge<ProcessEdgeType, ProcessUniversalEdgeProps>[];
  }) => {
    nodes: RuntimeNode[];
    edges: RuntimeEdge[];
  };
}

const getNodeValue = (node: RuntimeNode, key: string): unknown => {
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const props = (node.props as Record<string, unknown>) ?? {};
  const nodeRecord = node as unknown as Record<string, unknown>;
  return props[key] ?? attrs[key] ?? nodeRecord[key];
};

const getLabel = (node: RuntimeNode): string | undefined => {
  const text = typeof node.text === 'string' ? node.text : undefined;
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const attrsLabel = typeof attrs.label === 'string' ? (attrs.label as string) : undefined;
  return text ?? attrsLabel ?? (node.name as string | undefined);
};

const getNodePropsRecord = (node: RuntimeNode): Record<string, unknown> => {
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const props = (node.props as Record<string, unknown>) ?? {};
  const cleanProps = { ...props };
  delete cleanProps.attrs;
  delete cleanProps.text;
  return {
    ...attrs,
    ...cleanProps,
  };
};

export const processHydrator: ProcessHydrator = {
  toUniversal(nodes, edges) {
    const uNodes = nodes.map((node) => {
      const label = getLabel(node);
      const rawProps = getNodePropsRecord(node);
      return {
        id: node.id,
        type: (node.type as ProcessNodeType) ?? 'process',
        label,
        props: {
          ...rawProps,
          processId: (getNodeValue(node, 'processId') as string | undefined) ?? node.id,
          name: (getNodeValue(node, 'name') as string | undefined) ?? label,
          description: getNodeValue(node, 'description') as string | undefined,
          status: getNodeValue(node, 'status') as string | undefined,
          owner: getNodeValue(node, 'owner') as string | undefined,
          duration: getNodeValue(node, 'duration') as string | undefined,
          triggerType: getNodeValue(node, 'triggerType') as string | undefined,
          outcomeType: getNodeValue(node, 'outcomeType') as string | undefined,
          sla: getNodeValue(node, 'sla') as string | undefined,
          systems: getNodeValue(node, 'systems') as string[] | undefined,
          controls: getNodeValue(node, 'controls') as string[] | undefined,
          evidence: getNodeValue(node, 'evidence') as Array<Record<string, unknown>> | undefined,
          findings: getNodeValue(node, 'findings') as Array<Record<string, unknown>> | undefined,
          inputs: getNodeValue(node, 'inputs') as string | undefined,
          outputs: getNodeValue(node, 'outputs') as string | undefined,
          automationStatus: getNodeValue(node, 'automationStatus') as string | undefined,
          automationTool: getNodeValue(node, 'automationTool') as string | undefined,
          automationNotes: getNodeValue(node, 'automationNotes') as string | undefined,
          delegation: getNodeValue(node, 'delegation') as string | undefined,
          escalationPath: getNodeValue(node, 'escalationPath') as string | undefined,
          thirdPartyDependencies: getNodeValue(node, 'thirdPartyDependencies') as string[] | undefined,
          botDependencies: getNodeValue(node, 'botDependencies') as string[] | undefined,
          modelDependencies: getNodeValue(node, 'modelDependencies') as string[] | undefined,
          tags: getNodeValue(node, 'tags') as string[] | undefined,
          linkedDiagramId: getNodeValue(node, 'linkedDiagramId') as string | undefined,
        },
      };
    });

    const uEdges = edges.map((edge) => {
      const attrs = (edge.attrs as Record<string, unknown> | undefined) ?? {};
      const cleanAttrs = stripRelationInternalAttrs(attrs);
      const subtypeId = relationSubtypeFromAttrs('flowchart', edge.type, attrs) ?? edge.type;
      const props = {
        label: cleanAttrs.label as string | undefined,
        ...cleanAttrs,
      };
      const semantics = relationSemanticsForSubtype({
        diagramType: 'flowchart',
        subtypeId,
        qualifierSource: props,
        fallbackPredicate: edge.type,
        originalLabel: subtypeId,
        existing: storedRelationSemantics(attrs),
      });
      const semanticType = semantics.predicate === 'message_flow' ||
        semantics.predicate === 'data_flow' ||
        semantics.predicate === 'sequence_flow'
        ? semantics.predicate
        : 'association';
      return {
        id: edge.id,
        type: semanticType as ProcessEdgeType,
        from: edge.from,
        to: edge.to,
        props,
        semantics,
      };
    });

    return { nodes: uNodes, edges: uEdges };
  },

  fromUniversal(graph) {
    const nodes: RuntimeNode[] = graph.nodes.map((node, index) => {
      const name = (node.props.name as string | undefined) ?? node.label;
      const rawProps = {
        ...(node.props as Record<string, unknown>),
      };
      delete rawProps.attrs;
      delete rawProps.text;
      const attrs: Record<string, unknown> = {
        ...rawProps,
        label: name,
      };

      return {
        id: node.id,
        type: node.type,
        category: 'flowchart',
        name,
        text: name,
        x: 120 + (index % 5) * 220,
        y: 120 + Math.floor(index / 5) * 140,
        w: 140,
        h: 80,
        width: 140,
        height: 80,
        attrs,
        props: {
          ...node.props,
          text: name,
          attrs,
        },
      };
    });

    const edges: RuntimeEdge[] = graph.edges.map((edge) => {
      const semanticPredicate = edge.semantics?.predicate;
      const subtypeId = typeof semanticPredicate === 'string'
        ? relationSubtypeForPredicate('flowchart', semanticPredicate) ?? edge.type
        : edge.type;
      const mapping = getRelationshipMapping('flowchart', subtypeId);
      const edgeType = mapping?.edgeType || 'sequence_flow';
      const defaultEdgeProps = (mapping?.defaultEdgeProps ?? {}) as Record<string, unknown>;
      return {
        id: edge.id,
        type: edgeType as RuntimeEdge['type'],
        from: edge.from,
        to: edge.to,
        attrs: attrsWithRelationSemantics({
          ...defaultEdgeProps,
          ...(edge.props as Record<string, unknown>),
        }, edge.semantics, subtypeId),
      };
    });

    return { nodes, edges };
  },
};
